import { useCallback, useRef, useState } from "react";
import {
  ReactFlow,
  useNodesState,
  useEdgesState,
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  ConnectionLineType,
  ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/base.css";
import { useEffect } from "react";
import { Icon } from "@iconify/react";
import dagre from "@dagrejs/dagre";

import { Trigger, Workflow, Agent, Team } from "@/stores/WorkflowStore.ts";
import { logger } from "@/telemetry";

import AgentNode from "./AgentNode.tsx";
import InputNode from "./InputNode.tsx";
import ToolNode from "./ToolNode.tsx";
import TeamNode from "./TeamNode.tsx";

const nodeTypes = {
  agent: AgentNode,
  input: InputNode,
  tool: ToolNode,
  team: TeamNode,
};

const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
const nodeWidth = 100;
const nodeHeight = 150;
// Team nodes start collapsed (same size as agents), expand on click
// Using slightly larger defaults to accommodate team info
const teamNodeWidth = 120;
const teamNodeHeight = 160;

const getLayoutedElements = (nodes: any[], edges: any[], direction = "TB") => {
  const isHorizontal = direction === "LR";

  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    // Use larger dimensions for team nodes
    const width = node.type === "team" ? teamNodeWidth : nodeWidth;
    const height = node.type === "team" ? teamNodeHeight : nodeHeight;

    dagreGraph.setNode(node.id, { width, height });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const newNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const width = node.type === "team" ? teamNodeWidth : nodeWidth;
    const height = node.type === "team" ? teamNodeHeight : nodeHeight;

    return {
      ...node,
      targetPosition: isHorizontal ? "left" : "top",
      sourcePosition: isHorizontal ? "right" : "bottom",
      position: {
        x: nodeWithPosition.x - width / 2,
        y: nodeWithPosition.y - height / 2,
      },
    };
  });

  return { nodes: newNodes, edges };
};

const getNodes = (workflow: Workflow) => {
  // Get set of agents that belong to teams (to exclude from direct rendering)
  // But DON'T exclude sub-teams - they should be rendered as separate team nodes
  const teamMemberIds = new Set<string>();
  const teamIds = new Set<string>(workflow.teams ? Object.keys(workflow.teams) : []);

  if (workflow.teams) {
    Object.values(workflow.teams).forEach((team: Team) => {
      team.members.forEach((memberId) => {
        // Only exclude agents, not sub-teams
        if (!teamIds.has(memberId)) {
          teamMemberIds.add(memberId);
        }
      });
    });
  }

  // Create nodes for agents that are NOT team members
  const nodes = Object.entries(workflow.agents)
    .filter(([agentId]) => !teamMemberIds.has(agentId))
    .map(([agentId, agent]: [string, Agent]) => ({
      id: agentId,
      type: "agent",
      data: {
        name: agentId,
        type: agent.type,
        parameters: agent.parameters,
        emoji: <Icon className="text-xl" icon="fluent-color:agents-48" />,
      },
      position: { x: 0, y: 0 },
    }));

  // Create nodes for teams with member data included
  const teamNodes = workflow.teams
    ? Object.entries(workflow.teams).map(([teamId, team]: [string, Team]) => {
        // Build member data object with agent details and tools
        // Also handle sub-teams as members
        const memberData: Record<string, any> = {};

        team.members.forEach((memberId) => {
          // Check if member is a sub-team
          const subTeam = workflow.teams?.[memberId];

          if (subTeam) {
            // Sub-team member - mark it as a team reference
            memberData[memberId] = {
              name: memberId,
              type: "team",
              isSubTeam: true,
              strategy: subTeam.strategy,
              goal: subTeam.goal,
              memberCount: subTeam.members.length,
            };
          } else {
            // Regular agent member
            const agent = workflow.agents[memberId];

            if (agent) {
              memberData[memberId] = {
                name: memberId,
                type: agent.type,
                parameters: agent.parameters,
                tools: agent.parameters?.tools || [],
              };
            } else {
              logger.warn("WorkflowViewer: Team references member not found in agents or teams", {
                teamId,
                memberId,
              });
              // Add error entry for missing member so TeamNode can display visual indicator
              memberData[memberId] = {
                name: memberId,
                type: "unknown",
                notFound: true,
              };
            }
          }
        });

        return {
          id: teamId,
          type: "team",
          data: {
            name: teamId,
            members: team.members,
            memberData: memberData,
            goal: team.goal,
            maxCycles: team.maxCycles,
            strategy: team.strategy,
            model: team.model,
            emoji: <Icon className="text-xl" icon="fluent-color:people-team-48" />,
          },
          position: { x: 0, y: 0 },
        };
      })
    : [];

  // Create tool nodes only for non-team-member agents
  // Team member tools are now displayed inside the team node
  const toolNodes = Object.entries(workflow.agents)
    .filter(([agentId]) => !teamMemberIds.has(agentId))
    .flatMap(([_, agent]) =>
      (agent.parameters?.tools || []).map((toolId: string) => ({
        id: `${toolId}`,
        type: "tool",
        data: { name: toolId },
        position: { x: 0, y: 0 },
      })),
    );

  const inputNode = {
    id: "input",
    type: "input",
    data: {
      name: "Input",
      parameters: workflow.input,
      emoji: <Icon className="text-xl" icon="twemoji:inbox-tray" />,
    },
    position: { x: 0, y: 0 },
  };

  return [...nodes, ...teamNodes, ...toolNodes, inputNode];
};

const getEdges = (workflow: Workflow) => {
  // Get set of all team IDs
  const teamIds = new Set<string>(workflow.teams ? Object.keys(workflow.teams) : []);

  // Get set of agents that belong to teams (excluding sub-teams)
  const teamMemberIds = new Set<string>();

  if (workflow.teams) {
    Object.values(workflow.teams).forEach((team: Team) => {
      team.members.forEach((memberId) => {
        if (!teamIds.has(memberId)) {
          teamMemberIds.add(memberId);
        }
      });
    });
  }

  // Create edges for non-team-member agents based on their triggers
  const agent_edges = Object.entries(workflow.agents)
    .filter(([agentId]) => !teamMemberIds.has(agentId))
    .flatMap(([agentId, agent]) =>
      (agent.triggers || []).map((trigger: Trigger) => ({
        id: `${agentId}-${trigger.source}`,
        source: trigger.source,
        target: agentId,
      })),
    );

  // Create edges for teams based on their triggers
  const team_edges = workflow.teams
    ? Object.entries(workflow.teams).flatMap(([teamId, team]: [string, Team]) =>
        (team.triggers || []).map((trigger: Trigger) => ({
          id: `${teamId}-${trigger.source}`,
          source: trigger.source,
          target: teamId,
        })),
      )
    : [];

  // Create edges from parent teams to sub-team members
  // This shows the hierarchical relationship between teams
  const subteam_edges = workflow.teams
    ? Object.entries(workflow.teams).flatMap(([teamId, team]: [string, Team]) =>
        team.members
          .filter((memberId) => teamIds.has(memberId)) // Only sub-teams
          .map((subTeamId) => ({
            id: `${teamId}-subteam-${subTeamId}`,
            source: teamId,
            target: subTeamId,
            animated: true, // Animate to show it's a special relationship
            style: { strokeDasharray: "5,5" }, // Dashed line for hierarchy
          })),
      )
    : [];

  // Create edges for tools - only for non-team-member agents
  // Team member tools are displayed inside the team node, so no edges needed
  const tool_edges = Object.entries(workflow.agents)
    .filter(([agentId]) => !teamMemberIds.has(agentId))
    .flatMap(([agentId, agent]) =>
      (agent.parameters?.tools || []).map((toolId: string) => ({
        id: `${agentId}-${toolId}`,
        source: agentId,
        target: toolId,
      })),
    );

  return [...agent_edges, ...team_edges, ...subteam_edges, ...tool_edges];
};

const WorkflowViewer = ({ workflow }: { workflow: Workflow }) => {
  const [nodesState, setNodes] = useNodesState([]);
  const [edgesState, setEdges] = useEdgesState([]);
  const [error, setError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);

  useEffect(() => {
    try {
      const nodes = getNodes(workflow);
      const edges = getEdges(workflow);
      const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(nodes, edges);

      setNodes(layoutedNodes as any);
      setEdges(layoutedEdges as any);
    } catch (error) {
      setError(error as string);
      logger.error("Error getting nodes and edges", error);
    }
  }, [workflow]);

  const onNodesChange = useCallback((changes: any) => {
    setNodes((nds: any) => applyNodeChanges(changes, nds));
  }, []);

  const onEdgesChange = useCallback((changes: any) => {
    setEdges((eds: any) => applyEdgeChanges(changes, eds));
  }, []);

  const onConnect = useCallback((params: any) => {
    logger.info("onConnect", { params: String(params) });
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const onInit = useCallback((instance: ReactFlowInstance) => {
    reactFlowInstance.current = instance;
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (reactFlowInstance.current) {
        reactFlowInstance.current.fitView();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (error) {
    return (
      <div className="text-danger">
        <Icon className="mr-2" icon="gravity-ui:warning-triangle" />
        Cannot display workflow: {error.toString()}
      </div>
    );
  }

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div ref={containerRef} style={{ height: "100%", width: "100%" }}>
      <ReactFlow
        className="bg-teal-50 dark:bg-default-50"
        connectionLineType={ConnectionLineType.SmoothStep}
        defaultViewport={{ x: 50, y: 0, zoom: 1 }}
        edges={edgesState}
        fitView={false}
        maxZoom={2}
        minZoom={0.1}
        nodeTypes={nodeTypes}
        nodes={nodesState}
        onConnect={onConnect}
        onEdgesChange={onEdgesChange}
        onInit={onInit}
        onNodesChange={onNodesChange}
      >
        <Background />
        <Controls>
          <button
            className="react-flow__controls-button"
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            onClick={toggleFullscreen}
          >
            <Icon icon={isFullscreen ? "mdi:fullscreen-exit" : "mdi:fullscreen"} />
          </button>
        </Controls>
      </ReactFlow>
    </div>
  );
};

export default WorkflowViewer;
